import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, ChevronDown, Instagram, Menu, Search, ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Logo } from "@/components/Logo";
import { CollectionList } from "@/components/CollectionList";
import { ANNOUNCEMENT, COLLECTIONS, INSTAGRAM, SHOP, collectionUrl } from "@/data/catalog";
import { EASE, scrollToId } from "@/lib/scroll";
import { useLiveMeta } from "@/lib/live";
import { SaleStrip } from "@/components/SaleStrip";

const NAV = [
  ["Best Sellers", "bestsellers"],
  ["New", "new"],
  ["Shade Lab", "shade-lab"],
  ["Blender", "blender"],
  ["Brushes", "brushes"],
  ["Reviews", "reviews"],
];

/** One message; when it is wider than the bar (phones) it glides left and back instead of being clipped. */
const AnnouncementText = ({ text }) => {
  const ref = useRef(null);
  const [shift, setShift] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    const measure = () => setShift(Math.max(0, el.scrollWidth - el.parentElement.clientWidth));
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [text]);
  return (
    <span
      ref={ref}
      className={`mx-auto inline-block whitespace-nowrap ${shift ? "announcement-glide" : ""}`}
      style={shift ? { "--shift": `-${shift + 8}px`, "--dur": `${Math.max(6, shift / 22)}s` } : undefined}
    >
      {text}
    </span>
  );
};

/** Mirrors the store's own announcement bar (pulled live from acevanity.in); rotates when the store runs several messages. */
const AnnouncementBar = () => {
  const { announcements } = useLiveMeta();
  const messages = announcements?.length ? announcements : [ANNOUNCEMENT];
  const [index, setIndex] = useState(0);
  useEffect(() => {
    setIndex(0);
    const n = announcements?.length || 0;
    if (n < 2) return undefined;
    const id = setInterval(() => setIndex((i) => (i + 1) % n), 6000);
    return () => clearInterval(id);
  }, [announcements]);
  return (
    <div data-testid="announcement-bar" data-live={announcements?.length ? "true" : "false"} className="relative h-8 overflow-hidden bg-gold/95 px-4 font-mono text-[11px] uppercase tracking-[0.18em] text-ink sm:text-xs">
      <AnimatePresence initial={false} mode="wait">
        <motion.p
          key={messages[index]}
          data-testid="announcement-text"
          initial={{ y: 14, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -14, opacity: 0 }}
          transition={{ duration: 0.5, ease: EASE }}
          className="flex h-8 items-center"
        >
          <AnnouncementText text={messages[index]} />
        </motion.p>
      </AnimatePresence>
    </div>
  );
};

const MegaMenu = ({ onClose }) => {
  const [active, setActive] = useState(0);
  return (
    <motion.div
      data-testid="mega-menu"
      initial={{ opacity: 0, y: -12, clipPath: "inset(0% 0% 100% 0%)" }}
      animate={{ opacity: 1, y: 0, clipPath: "inset(0% 0% 0% 0%)" }}
      exit={{ opacity: 0, y: -8, transition: { duration: 0.25 } }}
      transition={{ duration: 0.6, ease: EASE }}
      className="absolute left-0 right-0 top-full border-b border-gold/20 bg-ink/95 backdrop-blur-xl"
      onMouseLeave={onClose}
    >
      <div className="mx-auto grid max-w-[1440px] grid-cols-12 gap-10 px-12 py-10">
        <ul className="col-span-7 grid grid-cols-2 gap-x-10">
          {COLLECTIONS.map((c, i) => (
            <li key={c.slug}>
              <a
                href={collectionUrl(c.handle)}
                data-testid={`mega-menu-link-${c.slug}`}
                onMouseEnter={() => setActive(i)}
                className="group flex items-baseline gap-4 border-b border-white/5 py-3 text-cream transition-colors hover:text-champagne"
              >
                <span className="font-mono text-xs text-stone">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-display text-2xl">{c.name}</span>
                {c.sub && <span className="text-xs uppercase tracking-[0.2em] text-stone">{c.sub}</span>}
                <ArrowUpRight className="ml-auto h-4 w-4 -translate-x-2 opacity-0 transition duration-500 group-hover:translate-x-0 group-hover:opacity-100" />
              </a>
            </li>
          ))}
        </ul>
        <div className="relative col-span-5 h-[320px] overflow-hidden">
          <AnimatePresence initial={false}>
            <motion.img
              key={COLLECTIONS[active].slug}
              src={COLLECTIONS[active].img}
              alt={COLLECTIONS[active].name}
              initial={{ clipPath: "inset(0% 0% 100% 0%)", scale: 1.1 }}
              animate={{ clipPath: "inset(0% 0% 0% 0%)", scale: 1 }}
              exit={{ opacity: 1, transition: { duration: 0.8 } }}
              transition={{ duration: 0.8, ease: EASE }}
              className="absolute inset-0 h-full w-full object-cover"
              style={{ objectPosition: COLLECTIONS[active].pos }}
            />
          </AnimatePresence>
          <div className="absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/80 to-transparent p-5">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-champagne">{COLLECTIONS[active].tag}</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const MobileMenu = () => {
  const [open, setOpen] = useState(false);
  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), 300);
  };
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button data-testid="mobile-menu-button" aria-label="Open menu" className="grid h-10 w-10 place-items-center rounded-full border border-white/10 text-cream lg:hidden">
          <Menu className="h-5 w-5" />
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[88vw] max-w-sm overflow-y-auto border-gold/20 bg-ink text-cream" data-lenis-prevent>
        <SheetTitle className="font-mono text-xs uppercase tracking-[0.3em] text-champagne">The Beauty Edit</SheetTitle>
        <CollectionList testPrefix="mobile-menu-collection" />
        <div className="mt-8 grid grid-cols-2 gap-2">
          {NAV.map(([label, id]) => (
            <button key={id} data-testid={`mobile-menu-nav-${id}`} onClick={() => go(id)} className="rounded-full border border-white/10 px-4 py-3 text-sm text-cream">
              {label}
            </button>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export const Header = () => {
  const [mega, setMega] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header data-testid="header-nav" className="fixed inset-x-0 top-0 z-50">
      <AnnouncementBar />
      <SaleStrip />
      <div className={`relative transition-[background-color,border-color] duration-500 ${scrolled || mega ? "border-b border-gold/15 bg-ink/80 backdrop-blur-xl" : "border-b border-transparent bg-transparent"}`}>
        <div className="mx-auto flex h-[68px] max-w-[1440px] items-center justify-between px-5 sm:px-8 lg:px-12">
          <Logo />
          <nav className="hidden items-center gap-8 lg:flex">
            <button
              data-testid="nav-collections-button"
              onMouseEnter={() => setMega(true)}
              onClick={() => setMega(true)}
              className="flex items-center gap-1.5 text-sm tracking-wide text-cream transition-colors hover:text-champagne"
            >
              Collections <ChevronDown className={`h-4 w-4 transition-transform duration-500 ${mega ? "rotate-180" : ""}`} />
            </button>
            {NAV.map(([label, id]) => (
              <button key={id} data-testid={`nav-link-${id}`} onClick={() => scrollToId(id)} onMouseEnter={() => setMega(false)} className="nav-link text-sm tracking-wide text-cream">
                {label}
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <a href={`${SHOP}/search`} data-testid="header-search-link" aria-label="Search the store" className="icon-btn hidden sm:grid"><Search className="h-4 w-4" /></a>
            <a href={INSTAGRAM} target="_blank" rel="noreferrer" data-testid="header-instagram-link" aria-label="Instagram" className="icon-btn hidden sm:grid"><Instagram className="h-4 w-4" /></a>
            <a href={`${SHOP}/cart`} data-testid="header-cart-link" aria-label="Your bag" className="icon-btn"><ShoppingBag className="h-4 w-4" /></a>
            <MobileMenu />
          </div>
        </div>
        <AnimatePresence>{mega && <MegaMenu onClose={() => setMega(false)} />}</AnimatePresence>
      </div>
    </header>
  );
};
